import produce from 'immer';
import { ProductOrder } from '@cdaas/api-clients/dist/order-openapi';

import { ApiError, Delivery, DeliveryMethodOfOrder, LocalBundleEnvelopeWithChosenMainVariant, User } from '../../types';
import * as actionTypes from './../actions/actionTypes';
import { ShopActions } from '../../types/actiontypes/shopActions';

export interface ShopReducer {
  loading: boolean;
  error: ApiError;
  cart: LocalBundleEnvelopeWithChosenMainVariant | null;
  delivery: Delivery;
  orderResponse: ProductOrder | null;
  selectedDeliveryMethod: DeliveryMethodOfOrder | null;
  orderOnBehalfUser: {
    status: boolean;
    userToOrderFor: User | null;
  };
  orderForOrganization: boolean;
}

export const initialState: ShopReducer = {
  loading: false,
  error: {
    status: false,
    message: null,
  },
  cart: null,
  delivery: {
    deliveryAddress: null,
    deliveryContact: null,
    isHomeDelivery: false,
    locationReference: null,
    invoicingAccountNumber: null,
  },
  orderResponse: null,
  selectedDeliveryMethod: null,
  orderOnBehalfUser: {
    status: false,
    userToOrderFor: null,
  },
  orderForOrganization: false,
};

const reducer = (state: ShopReducer = initialState, action: ShopActions): ShopReducer => {
  switch (action.type) {
    case actionTypes.SHOP_REQUEST_START:
      return produce(state, draft => {
        draft.loading = true;
      });
    case actionTypes.SHOP_SET_PRODUCT:
      return produce(state, draft => {
        draft.cart = action.product;
        draft.error = initialState.error;
      });
    case actionTypes.SHOP_SET_DELIVERY_DETAILS:
      return produce(state, draft => {
        draft.delivery.deliveryAddress = action.deliveryAddress;
        draft.delivery.deliveryContact = action.deliveryContact;
        draft.delivery.isHomeDelivery = action.isHomeDelivery;
        draft.delivery.locationReference = action.locationReference;
        draft.delivery.invoicingAccountNumber = action.invoicingAccountNumber;
      });
    case actionTypes.SHOP_REQUEST_FAILED:
      return produce(state, draft => {
        draft.loading = false;
        draft.error.status = true;
        draft.error.message = action.errorMessage;
      });
    case actionTypes.SHOP_PURCHASE_SUCCESS:
      return produce(state, draft => {
        draft.loading = false;
        draft.error = initialState.error;
      });
    case actionTypes.SHOP_RESET_SHOP:
      return produce(state, draft => {
        draft.loading = false;
        draft.cart = null;
        draft.error = initialState.error;
        draft.delivery = initialState.delivery;
        draft.orderResponse = initialState.orderResponse;
      });
    case actionTypes.SHOP_SET_ORDER_RESP:
      return produce(state, draft => {
        draft.orderResponse = action.orderResponse;
        draft.selectedDeliveryMethod = action.deliveryMethod;
      });
    case actionTypes.SHOP_RESET_ORDER_AND_DELIVERY:
      return produce(state, draft => {
        draft.orderResponse = initialState.orderResponse;
        draft.selectedDeliveryMethod = initialState.selectedDeliveryMethod;
        draft.delivery.locationReference = initialState.delivery.locationReference;
      });
    case actionTypes.SHOP_RESET_PRODUCT:
      return produce(state, draft => {
        draft.cart = initialState.cart;
      });
    case actionTypes.SHOP_SET_ORDER_ON_BEHALF_USER:
      return produce(state, draft => {
        draft.orderOnBehalfUser = {
          status: true,
          userToOrderFor: action.user,
        };
      });
    case actionTypes.SHOP_CLEAR_ORDER_ON_BEHALF_USER:
      return produce(state, draft => {
        draft.orderOnBehalfUser = initialState.orderOnBehalfUser;
        draft.delivery = initialState.delivery;
      });
    case actionTypes.SHOP_SET_ORDER_FOR_ORGANIZATION:
      return produce(state, draft => {
        draft.orderForOrganization = true;
      });
    case actionTypes.SHOP_CLEAR_ORDER_FOR_ORGANIZATION:
      return produce(state, draft => {
        draft.orderForOrganization = initialState.orderForOrganization;
      });
    default:
      return state;
  }
};

export default reducer;
